/**
 * The closing percentage: of the estimates that went out, how many closed.
 *
 * Jonathon, 2026-09-29: "we need to be able to track our closing percentage.
 * i think this should go on the master sold report. we track closing
 * percentage by estimate sent to estimate signed or approved/sent". Asked what
 * "approved/sent" meant: the Sends step, "it also needs to look at the signed
 * step". Asked which jobs a date range counts: those whose estimate was sent
 * in it, so a job sent in September that signs in October counts toward
 * September. And a line for each sales rep as well as the company.
 *
 * A job has been SENT when the prospect checklist's "Estimate Sent" is ticked.
 * It has CLOSED when "Signed Contract" is ticked, the contract was e-signed, it
 * went to production (Sends), or it sits in a sold stage — any one is enough.
 * A job that closed without "Estimate Sent" ever being ticked still had an
 * estimate go out, so it counts as sent too, dated the day it sold. Counting
 * it as closed but not sent could push the rate past 100%.
 */

import { soldDate } from "./listSummary.js";
import { dateFilterStart, type DateFilter } from "./search.js";
import { asLocalInstant } from "./signedDate.js";
import { SOLD_STAGES } from "./stage.js";
import type { JobStage } from "./types.js";

export interface ClosingJob {
  id: string;
  stage: JobStage;
  rep_id: string | null;
  rep?: { full_name: string | null } | null;
  signed_contract_at: string | null;
  sends_at: string | null;
}

/** When each job's two prospect steps were ticked, for the jobs where they were. */
export interface ClosingTicks {
  estimateSent: ReadonlyMap<string, string>;
  signedContract: ReadonlyMap<string, string>;
}

export interface ClosingRate {
  /** Jobs whose estimate went out in the window. */
  sent: number;
  /** Of those, the ones that have closed since. */
  closed: number;
  /** closed ÷ sent, 0–1. Zero when nothing was sent. */
  rate: number;
}

export interface RepClosingRate extends ClosingRate {
  repId: string | null;
  name: string;
}

export interface ClosingReport {
  company: ClosingRate;
  /** One per rep with anything sent in the window, highest rate first. */
  reps: RepClosingRate[];
}

const rateOf = (sent: number, closed: number): ClosingRate => ({
  sent,
  closed,
  rate: sent === 0 ? 0 : closed / sent,
});

export function hasClosed(job: ClosingJob, ticks: ClosingTicks): boolean {
  return (
    ticks.signedContract.has(job.id) ||
    Boolean(job.signed_contract_at) ||
    Boolean(job.sends_at) ||
    SOLD_STAGES.includes(job.stage)
  );
}

/** When the estimate went out, or null if it never did. */
export function estimateSentAt(
  job: ClosingJob,
  ticks: ClosingTicks,
): string | null {
  // "" is a tick with no time on it: sent, just not datable.
  const ticked = ticks.estimateSent.get(job.id);
  if (ticked !== undefined) return ticked;
  if (!hasClosed(job, ticks)) return null;
  return (
    (ticks.signedContract.get(job.id) || soldDate(job)) ??
    // A job moved to a sold stage by hand, with no date anywhere. It was sent
    // at some point; "all time" should count it, a dated window cannot.
    ""
  );
}

/**
 * The closing rate for the company and each rep, over the jobs given.
 *
 * Test jobs are the caller's to leave out, as they are everywhere else.
 */
export function closingReport(
  jobs: readonly ClosingJob[],
  ticks: ClosingTicks,
  filter: DateFilter,
  now: Date = new Date(),
): ClosingReport {
  const start = dateFilterStart(filter, now);
  const byRep = new Map<
    string,
    { name: string; sent: number; closed: number }
  >();
  let sent = 0;
  let closed = 0;

  for (const job of jobs) {
    const at = estimateSentAt(job, ticks);
    if (at === null) continue;
    // `signed_contract_at` is a `date` column, so `at` may be a bare
    // "2026-09-01": read as UTC that is the evening before, and the 1st of the
    // month falls out of "This month". `asLocalInstant` reads it as local.
    if (start && (at === "" || asLocalInstant(at) < start)) continue;

    const won = hasClosed(job, ticks);
    sent += 1;
    if (won) closed += 1;

    const key = job.rep_id ?? "";
    const rep = byRep.get(key) ?? {
      name: job.rep_id ? (job.rep?.full_name ?? "—") : "No rep",
      sent: 0,
      closed: 0,
    };
    rep.sent += 1;
    if (won) rep.closed += 1;
    byRep.set(key, rep);
  }

  const reps = [...byRep.entries()]
    .map(([key, r]) => ({
      repId: key || null,
      name: r.name,
      ...rateOf(r.sent, r.closed),
    }))
    .sort((a, b) => b.rate - a.rate || b.sent - a.sent);

  return { company: rateOf(sent, closed), reps };
}
